import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import { businessToday } from "utils/businessDate";
import { partnerQueryKeys } from "../../api/queryKeys";
import {
  bookPartnerAppointment,
  fetchPartnerAppointmentStaff,
  fetchPartnerClients,
} from "../../api/endpoints/workspace";

const EMPTY = {
  title: "",
  description: "",
  date: "",
  startTime: "",
  endTime: "",
  location: "",
  meetLink: "",
  clients: [],
  staffUserId: "",
};

const errorText = (error) =>
  error?.response?.data?.errorMessage ||
  error?.response?.data?.message ||
  "Could not book the appointment.";

/** Mirrors the server: own clients only, an office member, and today or later. */
export const bookingProblem = (form, today = businessToday()) => {
  if (!form.title.trim()) return "Give the appointment a title.";
  if (!form.clients.length) return "Choose at least one of your clients.";
  if (!form.staffUserId) return "Choose who from the office you are meeting.";
  if (!form.date) return "Pick a date.";
  if (form.date < today) return "Pick today or a later date.";
  if (!form.startTime) return "Pick a start time.";
  if (form.endTime && form.endTime <= form.startTime)
    return "The end time must be after the start.";
  if (form.meetLink.trim() && !/^https?:\/\//i.test(form.meetLink.trim())) {
    return "The meeting link must start with http:// or https://.";
  }
  return null;
};

const useClientSearch = (open) => {
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setSearch(input.trim()), 300);
    return () => clearTimeout(timer);
  }, [input]);
  const params = { page: 0, size: 20, search: search || undefined };
  const query = useQuery(partnerQueryKeys.clients(params), () => fetchPartnerClients(params), {
    enabled: open,
    keepPreviousData: true,
  });
  return { setInput, options: query.data?.content || [], loading: query.isFetching };
};

/**
 * A partner books a meeting between their own clients and one office member. It goes into the
 * organization's calendar where every staff member can see it, and the clients get the usual
 * appointment email.
 */
const BookAppointmentDialog = ({ open, onClose, initialClients = [] }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const clientSearch = useClientSearch(open);
  const staff = useQuery(partnerQueryKeys.appointmentStaff(), fetchPartnerAppointmentStaff, {
    enabled: open,
  });

  useEffect(() => {
    // Opened from a client's page, that client is already chosen.
    if (open) setForm({ ...EMPTY, clients: initialClients });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const patch = (field) => (event) => setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const book = useMutation(
    () =>
      bookPartnerAppointment({
        title: form.title.trim(),
        description: form.description.trim() || null,
        date: form.date,
        startTime: form.startTime,
        endTime: form.endTime || null,
        location: form.location.trim() || null,
        meetLink: form.meetLink.trim() || null,
        clientIds: form.clients.map((client) => client.clientId),
        staffUserId: Number(form.staffUserId),
      }),
    {
      onSuccess: () => {
        toast.success("Appointment booked. The office can see it in their calendar.");
        queryClient.invalidateQueries([...partnerQueryKeys.all, "appointments"]);
        onClose();
      },
      onError: (error) => toast.error(errorText(error)),
    }
  );

  const submit = () => {
    const problem = bookingProblem(form);
    if (problem) {
      toast.error(problem);
      return;
    }
    book.mutate();
  };

  const today = businessToday();

  return (
    <Dialog open={open} onClose={book.isLoading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Book an appointment</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Everyone in the office can see appointments you book. Your clients get the usual
          appointment email.
        </Alert>
        <div className="grid grid-cols-1 gap-3 pt-1">
          <TextField
            label="Title"
            value={form.title}
            onChange={patch("title")}
            size="small"
            required
            inputProps={{ maxLength: 200 }}
          />
          <Autocomplete
            multiple
            options={clientSearch.options}
            value={form.clients}
            loading={clientSearch.loading}
            filterOptions={(options) => options}
            getOptionLabel={(client) => client.name || `Client #${client.clientId}`}
            isOptionEqualToValue={(option, value) => option.clientId === value.clientId}
            onInputChange={(_, value) => clientSearch.setInput(value)}
            onChange={(_, value) => setForm((prev) => ({ ...prev, clients: value.slice(0, 10) }))}
            noOptionsText="None of your clients match"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Your clients"
                size="small"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {clientSearch.loading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <TextField
            select
            label="With (office)"
            value={form.staffUserId}
            onChange={patch("staffUserId")}
            size="small"
            required
            helperText={
              staff.isError ? "Could not load the office list." : "Admin, account or super admin"
            }
          >
            {(staff.data || []).map((member) => (
              <MenuItem key={member.id} value={String(member.id)}>
                {member.name}
              </MenuItem>
            ))}
          </TextField>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <TextField
              label="Date"
              type="date"
              value={form.date}
              onChange={patch("date")}
              size="small"
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: today }}
            />
            <TextField
              label="Start"
              type="time"
              value={form.startTime}
              onChange={patch("startTime")}
              size="small"
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End"
              type="time"
              value={form.endTime}
              onChange={patch("endTime")}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <TextField
            label="Location (optional)"
            value={form.location}
            onChange={patch("location")}
            size="small"
          />
          <TextField
            label="Meeting link (optional)"
            value={form.meetLink}
            onChange={patch("meetLink")}
            size="small"
          />
          <TextField
            label="Notes for the meeting (optional)"
            value={form.description}
            onChange={patch("description")}
            size="small"
            multiline
            minRows={2}
            inputProps={{ maxLength: 4000 }}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={book.isLoading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={book.isLoading}>
          {book.isLoading ? "Booking…" : "Book appointment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookAppointmentDialog;
